import { useEffect, useState } from 'react';
import { Edit2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminCreateSale, adminDeleteSale, adminUpdateSale, getAdminSalesHub, getAdminSettings } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SaleRecordModal } from './SaleRecordModal';

const currency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const sourceLabel = (source) => source.charAt(0).toUpperCase() + source.slice(1);

export const SalesHubTab = ({ collectibles }) => {
  const [hub, setHub] = useState({ summary: {}, sales: [] });
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('all');
  const [gst, setGst] = useState(3);
  const [modal, setModal] = useState({ open: false, sale: null });

  const load = async () => {
    setLoading(true);
    try {
      const [hubResponse, settingsResponse] = await Promise.all([getAdminSalesHub(), getAdminSettings()]);
      setHub(hubResponse.data);
      setGst(settingsResponse.data.gst_percent ?? 3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to load Sales Hub');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (modal.sale) await adminUpdateSale(modal.sale.sale_id, data);
      else await adminCreateSale(data);
      toast.success(modal.sale ? 'Sale updated' : 'Sale recorded');
      setModal({ open: false, sale: null });
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to save sale');
    }
  };

  const remove = async (sale) => {
    if (!window.confirm(`Delete ${sale.product_name} sale record?`)) return;
    try {
      await adminDeleteSale(sale.sale_id);
      toast.success('Sale deleted');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to delete sale');
    }
  };

  const sales = source === 'all' ? hub.sales : hub.sales.filter((sale) => sale.source === source);
  const summary = hub.summary || {};

  if (loading) return <div className="py-16 text-center text-muted-foreground" data-testid="sales-hub-loading">Loading Sales Hub…</div>;

  return (
    <div data-testid="sales-hub-tab">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
        <div><h3 className="font-display text-2xl font-bold">Sales Hub</h3><p className="text-sm text-muted-foreground">Website and direct-channel sales in one ledger</p></div>
        <div className="flex gap-2">
          <Select value={source} onValueChange={setSource}><SelectTrigger className="w-40" data-testid="sales-source-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All channels</SelectItem>{['website', 'whatsapp', 'instagram', 'phone', 'offline', 'other'].map((item) => <SelectItem key={item} value={item}>{sourceLabel(item)}</SelectItem>)}</SelectContent></Select>
          <Button onClick={() => setModal({ open: true, sale: null })} data-testid="record-sale-button"><Plus className="w-4 h-4 mr-1" />Record Sale</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Metric label="Sales" value={summary.total_sales || 0} testId="sales-total-count" />
        <Metric label="Revenue" value={currency(summary.total_revenue)} testId="sales-total-revenue" />
        <Metric label="Supplier Cost" value={currency(summary.total_supplier_cost)} testId="sales-total-supplier" />
        <Metric label="SŌHM Margin" value={currency(summary.total_sohm_margin)} testId="sales-total-margin" />
        <Metric label="GST" value={currency(summary.total_gst)} testId="sales-total-gst" />
      </div>
      <div className="space-y-3">
        {sales.map((sale) => <SaleRow key={`${sale.record_type}-${sale.sale_id}`} sale={sale} onEdit={() => setModal({ open: true, sale })} onDelete={() => remove(sale)} />)}
        {sales.length === 0 && <p className="text-center text-muted-foreground py-12" data-testid="sales-empty-state">No sales recorded for this channel.</p>}
      </div>
      <SaleRecordModal open={modal.open} sale={modal.sale} collectibles={collectibles} defaultGst={gst} onClose={() => setModal({ open: false, sale: null })} onSave={save} />
    </div>
  );
};

const Metric = ({ label, value, testId }) => <div className="border border-border/40 bg-card p-4" data-testid={testId}><p className="text-xs text-muted-foreground uppercase">{label}</p><p className="font-display text-xl font-bold mt-1">{value}</p></div>;

const SaleRow = ({ sale, onEdit, onDelete }) => (
  <div className="border border-border/40 bg-card p-4 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`sale-row-${sale.sale_id}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1"><Badge variant="outline">{sourceLabel(sale.source)}</Badge><Badge variant="outline">{sale.payment_status}</Badge><span className="text-xs text-muted-foreground">{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('en-IN') : ''}</span></div>
      <p className="font-semibold truncate">{sale.product_name}</p><p className="text-xs text-muted-foreground">{sale.customer_name} · Qty {sale.quantity || 1}</p>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-1 text-sm">
      <SmallAmount label="Supplier" value={sale.supplier_cost} /><SmallAmount label="Margin" value={sale.sohm_margin} /><SmallAmount label="GST" value={sale.gst} /><SmallAmount label="Final" value={sale.final_price} />
    </div>
    {sale.record_type === 'manual' && <div className="flex gap-1"><Button size="icon" variant="ghost" onClick={onEdit} data-testid={`edit-sale-${sale.sale_id}`}><Edit2 className="w-4 h-4" /></Button><Button size="icon" variant="ghost" className="text-destructive" onClick={onDelete} data-testid={`delete-sale-${sale.sale_id}`}><Trash2 className="w-4 h-4" /></Button></div>}
  </div>
);

const SmallAmount = ({ label, value }) => <div><p className="text-[10px] text-muted-foreground uppercase">{label}</p><p className="font-mono">{currency(value)}</p></div>;